import { getServerError } from "@edgetier/utilities";
import { IActiveTranslation, ILanguage, Route } from "@edgetier/watchtower-types";
import { useClient } from "hooks-for/clients/use-client";
import { useLanguages } from "hooks-for/filters/use-languages";
import { useUser } from "hooks-for/users/use-user";
import { useState } from "react";
import toast from "react-hot-toast";
import { requestTranslation } from "./use-translation";

/**
 * Hook used to handle all phrase trigger samples / results message translations.
 */
const usePhraseMessageTranslation = () => {
    // Store any active translations
    const [activeTranslations, setActiveTranslations] = useState<Record<string, IActiveTranslation>>({});

    const { data: user } = useUser();
    const { data: client } = useClient(user?.clientId!);
    const { data: languages } = useLanguages(
        {},
        { staleTime: 1000 * 60 * 60 * 24, enabled: client?.isTranslationEnabled }
    );

    // Group languages by ISO code
    const languagesByIsoCode: Record<string, ILanguage> = (languages ?? []).reduce(
        (previousLanguages, language) => ({ ...previousLanguages, [language.languageIsoCode]: language }),
        {}
    );

    const userLanguage = languagesByIsoCode[user?.languageIsoCode!];

    /**
     * Display translation in user's language when button is pressed
     * @param phraseMessageId       Element (phrase sample / result) ID, used for indexing
     * @param text                  Text which will be translated
     * @param sourceLanguageIsoCode Language source for text
     */
    const toggleTranslation = async (phraseMessageId: string, text: string, sourceLanguageIsoCode: string) => {
        const currentPhraseTranslation = activeTranslations[phraseMessageId];

        try {
            if (typeof currentPhraseTranslation === "undefined") {
                setActiveTranslations((translations) => ({
                    ...translations,
                    [phraseMessageId]: {
                        translatedText: "",
                        isTranslated: false,
                        isLoading: true,
                    },
                }));

                // Request a translation if doesn't exits, then toggle it on.
                const translation = await requestTranslation(Route.Translations, {
                    text,
                    sourceLanguageIsoCode,
                    targetLanguageIsoCode: userLanguage.languageIsoCode,
                });

                setActiveTranslations((translations) => ({
                    ...translations,
                    [phraseMessageId]: {
                        translatedText: translation.translatedText,
                        isTranslated: true,
                        isLoading: false,
                    },
                }));
            } else {
                // If a translation exists, toggle it.
                setActiveTranslations((translations) => ({
                    ...translations,
                    [phraseMessageId]: {
                        ...currentPhraseTranslation,
                        isTranslated: !currentPhraseTranslation.isTranslated,
                    },
                }));

                // Display translation toast error messages if the request failed
                if (!currentPhraseTranslation.isTranslated && currentPhraseTranslation.isError) {
                    toast.error(`Translation Error`, { id: "translationError" });
                }
            }
        } catch (error) {
            // Mark translation as error
            setActiveTranslations((translations) => ({
                ...translations,
                [phraseMessageId]: {
                    translatedText: "",
                    isTranslated: true,
                    isLoading: false,
                    isError: true,
                },
            }));

            toast.error(`Translation Error: ${getServerError(error)}`, { id: "translationError" });
        }
    };

    return {
        activeTranslations,
        languagesByIsoCode,
        toggleTranslation,
        userLanguage,
        isTranslationEnabled: client?.isTranslationEnabled ?? false,
    };
};

export default usePhraseMessageTranslation;
